import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, Sun, Smartphone, Settings, Check, Shield, ShieldCheck, Palette } from 'lucide-react';
import { services } from '../mockData';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const iconMap = {
  'Sparkles': Sparkles,
  'Sun': Sun,
  'Smartphone': Smartphone,
  'Settings': Settings,
  'Shield': Shield,
  'ShieldCheck': ShieldCheck,
  'Palette': Palette
};

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Our Services</h1>
          <p className="page-subtitle">
            Professional automotive care and enhancement services tailored to your needs
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="services-details-section">
        <div className="container">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div 
                key={service.id} 
                id={service.slug}
                className={`service-detail ${index % 2 === 1 ? 'reverse' : ''}`}
              >
                <div className="service-detail-content">
                  <div className="service-detail-icon">
                    <IconComponent size={48} />
                  </div>
                  <h2 className="service-detail-title">{service.name}</h2>
                  <p className="service-detail-description">{service.description}</p>
                  
                  {/* Features List */}
                  <div className="service-features">
                    <h3>What's Included:</h3>
                    <ul className="features-list">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <Check size={20} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Packages */}
                  <div className="service-packages">
                    <h3>Service Packages:</h3>
                    <div className="packages-grid">
                      {service.packages.map((pkg, idx) => (
                        <div key={idx} className="package-card">
                          <h4 className="package-name">{pkg.name}</h4>
                          <p className="package-price">{pkg.price}</p>
                          <p className="package-duration">{pkg.duration}</p>
                          <ul className="package-includes">
                            {pkg.includes.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                          <a 
                            href={`tel:6473261174`} 
                            className="btn-primary"
                          >
                            Book This Package
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title text-center">Frequently Asked Questions</h2>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>How long does a full detail take?</AccordionTrigger>
                <AccordionContent>
                  Depending on the package, a full detail can take anywhere from 2-3 hours for a basic detail 
                  to a full day for our ultimate package with paint correction and ceramic coating.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>Is window tinting legal in Ontario?</AccordionTrigger>
                <AccordionContent>
                  Yes! We ensure all our tinting complies with Ontario regulations. Front side windows must allow 
                  at least 70% light transmission, while rear windows can be darker. We'll help you choose the right shade.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>Will CarPlay work with my older vehicle?</AccordionTrigger>
                <AccordionContent>
                  Absolutely! We can install CarPlay/Android Auto in virtually any vehicle. Our systems integrate 
                  seamlessly and often retain your factory steering wheel controls.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>Do you offer mobile service?</AccordionTrigger>
                <AccordionContent>
                  For certain services like detailing, we can come to you! Contact us via WhatsApp or phone 
                  to discuss mobile service availability in Kitchener, Waterloo, Cambridge, and Guelph.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5">
                <AccordionTrigger>What payment methods do you accept?</AccordionTrigger>
                <AccordionContent>
                  We accept cash, debit, all major credit cards, and e-transfer. Payment is due upon completion of service.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6">
                <AccordionTrigger>Do you provide warranties?</AccordionTrigger>
                <AccordionContent>
                  Yes! All our services come with warranties. Detailing ceramic coatings have up to 5-year warranties, 
                  window tinting includes lifetime warranties, and electronic installations come with 1-2 year warranties 
                  depending on the package.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
